import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import _ from 'lodash';

import {BucketedQueryState} from './bucketedQueryManager';

export type BucketedRunsReducerState = {
  aggregatedByRunsetId: Record<string, boolean>;
  loadingByRunSetId: Record<string, boolean>;
  isLoading: boolean;
  runSetsByRunsetId: Record<string, boolean>;
  runDataByRunsetId: Record<string, BucketedQueryState>;
  runNames: string[];
  totalRuns: number;
};
type BucketedRunsReducerAction =
  | {
      type: 'bucketedRuns/setLoadingByRunSetId';
      runsetId: string;
      loading: boolean;
    }
  | {
      type: 'bucketedRuns/setAggregatedByRunsetId';
      runsetId: string;
      isAggregated: boolean;
    }
  | {
      type: 'bucketedRuns/setRunSetEnabled';
      enabledStatusByRunsetId: Record<string, boolean>;
    }
  | {
      type: 'bucketedRuns/setRunDataByRunsetId';
      runsetId: string;
      data: BucketedQueryState;
    }
  | {
      type: 'bucketedRuns/setTotalRuns';
      totalRuns?: number;
    };

export function bucketedRunsReducer(
  state: BucketedRunsReducerState,
  action: BucketedRunsReducerAction
) {
  switch (action.type) {
    case 'bucketedRuns/setAggregatedByRunsetId': {
      /**
       * To avoid unnecessary renders, we only want to update the isAggregated state when the new state is different from the old state when both states are coerced to booleans:
       * 1. old state: undefined, new state: false => no update
       * 2. old state: false, new state: false => no update
       * 3. old state: false, new state: true => update
       * 4. old state: true, new state: false => update
       * 5. old state: true, new state: true => no update
       * 6. old state: true, new state: undefined => update
       * etc...
       */

      const prevValue = !!state.aggregatedByRunsetId[action.runsetId];
      const newValue = !!action.isAggregated;
      if (prevValue !== newValue) {
        return {
          ...state,
          aggregatedByRunsetId: {
            ...state.aggregatedByRunsetId,
            [action.runsetId]: action.isAggregated,
          },
        };
      }
      return state;
    }

    case 'bucketedRuns/setLoadingByRunSetId': {
      const oldState = state.loadingByRunSetId[action.runsetId];
      if (Boolean(oldState) === Boolean(action.loading)) {
        return state;
      }

      const newLoadingByRunSetId = {
        ...state.loadingByRunSetId,
        [action.runsetId]: action.loading,
      };
      return {
        ...state,
        loadingByRunSetId: newLoadingByRunSetId,
        isLoading: Object.values(newLoadingByRunSetId).some(Boolean),
      };
    }

    // This is a critical reducer computation: making sure we keep reference equality here when polling updates but no data changes is critical for making sure the UI doesn't thrash and try to render all the panels. The output state here is what ultimately gets serialized down into the cache as well.
    case 'bucketedRuns/setRunDataByRunsetId': {
      // this deep equals check is much, much cheaper than an extra render,
      // and much less costly than missing a subtle difference with a hand-rolled equality check,
      // so it's worth the price
      const hasChanged = !_.isEqual(
        state.runDataByRunsetId[action.runsetId]?.data?.runsById,
        action.data?.data?.runsById
      );

      if (hasChanged) {
        return {
          ...state,
          runDataByRunsetId: {
            ...state.runDataByRunsetId,
            [action.runsetId]: action.data ?? {},
          },
          runNames: Object.values(state.runDataByRunsetId)
            .flatMap(({data}) =>
              Object.values(data?.runsById ?? {}).map(r => r.name)
            )
            .filter(isNotNullOrUndefined),
        };
      }
      return state;
    }

    case 'bucketedRuns/setRunSetEnabled': {
      const hasChanged = !_.isEqual(
        state.runSetsByRunsetId,
        action.enabledStatusByRunsetId
      );
      if (hasChanged) {
        return {
          ...state,
          runSetsByRunsetId: action.enabledStatusByRunsetId,
        };
      }

      return state;
    }

    case 'bucketedRuns/setTotalRuns': {
      if (state.totalRuns === action.totalRuns) {
        return state;
      }

      return {
        ...state,
        totalRuns: action.totalRuns ?? 0,
      };
    }

    default: {
      return state;
    }
  }
}
